import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import MonthCalendar from '../components/MonthCalendar';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  parseISO, differenceInCalendarDays,
} from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
  CalendarClock, Plus, Check, X, Clock,
  Users, Trash2, RotateCcw, UserCheck,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import UserAvatar from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusStyles = {
  pending: 'bg-[#FEF3C7] text-[#B45309]',
  approved: 'bg-[#D1FAE5] text-[#047857]',
  rejected: 'bg-[#FEE2E2] text-[#D93025]',
};

const LeavePage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const getLocale = () => (i18n.language === 'de' ? de : enUS);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarVacations, setCalendarVacations] = useState([]);
  const [myVacations, setMyVacations] = useState([]);
  const [pending, setPending] = useState([]);
  const [substitutes, setSubstitutes] = useState([]);
  const [users, setUsers] = useState([]);

  // Create vacation dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', note: '', user_id: '' });
  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const monthRange = useMemo(() => {
    const start = format(startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    const end = format(endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    return { start, end };
  }, [currentMonth]);

  const fetchCalendar = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/vacations?start=${monthRange.start}&end=${monthRange.end}`, { headers: getAuthHeader() });
      setCalendarVacations(res.data);
    } catch (e) {
      console.error('Failed to fetch vacations', e);
    }
  }, [monthRange, getAuthHeader]);

  const fetchMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/vacations/mine`, { headers: getAuthHeader() });
      setMyVacations(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const fetchPending = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API_URL}/vacations/pending`, { headers: getAuthHeader() });
      setPending(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader, isAdmin]);

  const fetchSubstitutes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/substitutes`, { headers: getAuthHeader() });
      setSubstitutes(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(res.data);
    } catch (e) { console.error(e); }
  }, [getAuthHeader]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);
  useEffect(() => { fetchMine(); fetchPending(); fetchSubstitutes(); fetchUsers(); }, [fetchMine, fetchPending, fetchSubstitutes, fetchUsers]);

  const refreshAll = () => { fetchCalendar(); fetchMine(); fetchPending(); };

  const openCreate = (date = null) => {
    const d = date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
    setForm({ start_date: d, end_date: d, note: '', user_id: '' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error(t('leave.invalidDates'));
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error(t('leave.endBeforeStart'));
      return;
    }
    try {
      const payload = {
        start_date: form.start_date,
        end_date: form.end_date,
        note: form.note || null,
      };
      if (isAdmin && form.user_id && form.user_id !== user.id) payload.user_id = form.user_id;
      const res = await axios.post(`${API_URL}/vacations`, payload, { headers: getAuthHeader() });
      toast.success(res.data.status === 'approved' ? t('leave.createdApproved') : t('leave.created'));
      setCreateOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('leave.error'));
    }
  };

  const handleWithdraw = async (id) => {
    if (!window.confirm(t('leave.confirmWithdraw'))) return;
    try {
      await axios.delete(`${API_URL}/vacations/${id}`, { headers: getAuthHeader() });
      toast.success(t('leave.withdrawn'));
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('leave.error')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('leave.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/vacations/${id}`, { headers: getAuthHeader() });
      toast.success(t('leave.deleted'));
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('leave.error')); }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/vacations/${id}/decision`, { status: 'approved' }, { headers: getAuthHeader() });
      toast.success(t('leave.decided'));
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('leave.error')); }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await axios.put(`${API_URL}/vacations/${rejectTarget.id}/decision`, { status: 'rejected', reason: rejectReason || null }, { headers: getAuthHeader() });
      toast.success(t('leave.decided'));
      setRejectTarget(null);
      setRejectReason('');
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || t('leave.error')); }
  };

  const handleSetSubstitute = async (userId, substituteId) => {
    try {
      await axios.put(`${API_URL}/substitutes/${userId}`, { substitute_id: substituteId || null }, { headers: getAuthHeader() });
      toast.success(t('leave.substituteSaved'));
      fetchSubstitutes();
    } catch (e) { toast.error(e.response?.data?.detail || t('leave.error')); }
  };

  // ---- Calendar helpers ----
  const handleMonthChange = useCallback((monthDate) => {
    setCurrentMonth(monthDate);
  }, []);

  const vacationDays = (v) => differenceInCalendarDays(parseISO(v.end_date), parseISO(v.start_date)) + 1;

  const formatRange = (v) => {
    const opts = { locale: getLocale() };
    return `${format(parseISO(v.start_date), 'd. MMM', opts)} – ${format(parseISO(v.end_date), 'd. MMM yyyy', opts)}`;
  };

  const StatusBadge = ({ status }) => (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusStyles[status] || 'bg-zinc-200 text-zinc-600'}`}>
      {t(`leave.${status}`)}
    </span>
  );

  return (
    <Layout>
      <div data-testid="leave-page" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white shadow-sm">
              <CalendarClock className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('leave.title')}</h1>
              <p className="text-sm text-zinc-500">{t('leave.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => openCreate()}
            data-testid="new-vacation-btn"
            className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-4 shrink-0"
          >
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">{t('leave.requestVacation')}</span>
          </Button>
        </div>

        <Tabs defaultValue="calendar" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="calendar" data-testid="tab-calendar">{t('leave.tabCalendar')}</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">{t('leave.tabRequests')}</TabsTrigger>
            {isAdmin && <TabsTrigger value="substitutes" data-testid="tab-substitutes">{t('leave.tabSubstitutes')}</TabsTrigger>}
          </TabsList>

          {/* ============ CALENDAR (general: vacations + events) ============ */}
          <TabsContent value="calendar">
            {/* Legend */}
            <div className="px-1 py-2 mb-2 flex flex-wrap gap-4 text-xs text-zinc-600">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#10B981]" />{t('leave.legendApproved')}</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#F59E0B]" />{t('leave.legendPending')}</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#3B82F6]" />{t('leave.legendEvent')}</span>
            </div>

            <MonthCalendar
              vacations={calendarVacations}
              onMonthChange={handleMonthChange}
              title={t('nav.leaveShort')}
            />

            {/* Approved vacations detail list (with substitute) for the visible month */}
            <div className="mt-4 space-y-2">
              {calendarVacations.filter(v => v.status === 'approved').length === 0 && (
                <p className="text-sm text-zinc-400 text-center py-4">{t('leave.noRequests')}</p>
              )}
              {calendarVacations.filter(v => v.status === 'approved').map((v) => (
                <div key={v.id} className="bg-white rounded-xl border border-zinc-200 p-3 flex items-center gap-3" data-testid={`cal-vac-${v.id}`}>
                  <UserAvatar user={{ id: v.user_id, name: v.user_name }} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-zinc-900 truncate">{v.user_name}</div>
                    <div className="text-xs text-zinc-500">{formatRange(v)} · {t('leave.days', { count: vacationDays(v) })}</div>
                  </div>
                  {v.substitute_name && (
                    <div className="flex items-center gap-1.5 text-xs text-zinc-600 bg-zinc-100 rounded-full px-2.5 py-1 shrink-0">
                      <UserCheck className="w-3.5 h-3.5 text-[#047857]" />
                      <span className="truncate max-w-[120px]">{v.substitute_name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ============ REQUESTS ============ */}
          <TabsContent value="requests">
            {isAdmin && (
              <div className="mb-6">
                <h2 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#B45309]" />{t('leave.pendingRequests')}</h2>
                <div className="space-y-2">
                  {pending.length === 0 && <p className="text-sm text-zinc-400 py-3">{t('leave.noPending')}</p>}
                  {pending.map((v) => (
                    <div key={v.id} className="bg-white rounded-xl border border-zinc-200 p-3" data-testid={`pending-${v.id}`}>
                      <div className="flex items-center gap-3">
                        <UserAvatar user={{ id: v.user_id, name: v.user_name }} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-zinc-900 truncate">{v.user_name}</div>
                          <div className="text-xs text-zinc-500">{formatRange(v)} · {t('leave.days', { count: vacationDays(v) })}</div>
                          {v.note && <div className="text-xs text-zinc-600 mt-0.5 italic truncate">"{v.note}"</div>}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => handleApprove(v.id)} data-testid={`approve-${v.id}`} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-8 px-3"><Check className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t('leave.approve')}</span></Button>
                          <Button size="sm" variant="outline" onClick={() => { setRejectTarget(v); setRejectReason(''); }} data-testid={`reject-${v.id}`} className="rounded-full text-[#D93025] border-[#D93025] hover:bg-[#FEE2E2] h-8 px-3"><X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t('leave.reject')}</span></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="font-semibold text-zinc-900 mb-3">{t('leave.myRequests')}</h2>
            <div className="space-y-2">
              {myVacations.length === 0 && <p className="text-sm text-zinc-400 py-3">{t('leave.noRequests')}</p>}
              {myVacations.map((v) => (
                <div key={v.id} className="bg-white rounded-xl border border-zinc-200 p-3 flex items-center gap-3" data-testid={`my-vac-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-900">{formatRange(v)}</span>
                      <StatusBadge status={v.status} />
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{t('leave.days', { count: vacationDays(v) })}{v.substitute_name ? ` · ${t('leave.substituteFor')} ${v.substitute_name}` : ''}</div>
                    {v.note && <div className="text-xs text-zinc-600 mt-0.5 italic">"{v.note}"</div>}
                    {v.status === 'rejected' && v.reject_reason && <div className="text-xs text-[#D93025] mt-0.5">{v.reject_reason}</div>}
                  </div>
                  {v.status === 'pending' && (
                    <Button size="sm" variant="ghost" onClick={() => handleWithdraw(v.id)} data-testid={`withdraw-${v.id}`} className="rounded-full text-zinc-500 hover:text-[#D93025] hover:bg-[#FEE2E2] h-8 px-3"><RotateCcw className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{t('leave.withdraw')}</span></Button>
                  )}
                  {isAdmin && v.status === 'approved' && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(v.id)} className="rounded-full text-zinc-400 hover:text-[#D93025] hover:bg-[#FEE2E2] h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ============ SUBSTITUTES (admin) ============ */}
          {isAdmin && (
            <TabsContent value="substitutes">
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <div className="p-4 border-b border-zinc-200">
                  <h2 className="font-semibold text-zinc-900 flex items-center gap-2"><Users className="w-5 h-5 text-[#D93025]" />{t('leave.substitutesTitle')}</h2>
                  <p className="text-sm text-zinc-500 mt-0.5">{t('leave.substitutesHint')}</p>
                </div>
                <div className="divide-y divide-zinc-100">
                  {substitutes.map((row) => (
                    <div key={row.user_id} className="p-3 flex items-center gap-3" data-testid={`sub-row-${row.user_id}`}>
                      <UserAvatar user={{ id: row.user_id, name: row.user_name }} size="md" />
                      <div className="flex-1 min-w-0 font-medium text-zinc-900 truncate">{row.user_name}</div>
                      <div className="w-48 shrink-0">
                        <Select
                          value={row.substitute_id || 'none'}
                          onValueChange={(val) => handleSetSubstitute(row.user_id, val === 'none' ? null : val)}
                        >
                          <SelectTrigger data-testid={`sub-select-${row.user_id}`} className="h-9">
                            <SelectValue placeholder={t('leave.selectSubstitute')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('leave.noSubstitute')}</SelectItem>
                            {users.filter(u => u.id !== row.user_id).map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Create vacation dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="vac-create-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white"><CalendarClock className="w-5 h-5" /></div>
              {t('leave.newVacation')}
            </DialogTitle>
            <DialogDescription id="vac-create-desc">{t('leave.subtitle')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {isAdmin && (
              <div>
                <Label className="text-sm font-medium">{t('leave.vacationFor')}</Label>
                <Select value={form.user_id || user?.id} onValueChange={(val) => setForm({ ...form, user_id: val })}>
                  <SelectTrigger data-testid="vac-user-select" className="mt-1.5 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={user?.id}>{user?.name}</SelectItem>
                    {users.filter(u => u.id !== user?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">{t('leave.startDate')}</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date < e.target.value ? e.target.value : form.end_date })} className="mt-1.5 h-10" data-testid="vac-start-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">{t('leave.endDate')}</Label>
                <Input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 h-10" data-testid="vac-end-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">{t('leave.note')}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t('leave.notePlaceholder')} className="mt-1.5 resize-none" rows={2} data-testid="vac-note-input" />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full px-6">{t('common.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="vac-submit-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6">
              {isAdmin && form.user_id && form.user_id !== user?.id ? t('leave.submitAdmin') : t('leave.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent className="sm:max-w-md" aria-describedby="vac-reject-desc">
          <DialogHeader>
            <DialogTitle>{t('leave.rejectTitle')}</DialogTitle>
            <DialogDescription id="vac-reject-desc">
              {rejectTarget ? `${rejectTarget.user_name} · ${formatRange(rejectTarget)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-sm font-medium">{t('leave.rejectReason')}</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('leave.rejectReasonPh')} className="mt-1.5 resize-none" rows={3} data-testid="reject-reason-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }} className="rounded-full px-6">{t('common.cancel')}</Button>
            <Button onClick={handleReject} data-testid="reject-confirm-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6">{t('leave.confirmReject')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LeavePage;
